// zod/v4 (shipped alongside v3 by the zod package): the Anthropic SDK's
// `zodOutputFormat` helper takes v4 schemas, and the MOM content schema IS the
// structured-output schema — defining it twice would let the two drift.
import * as z from 'zod/v4';
/**
 * The house MOM, as structured data. One schema serves every meeting type:
 * the template definition (see the server's mom/templates) decides which
 * sections a type renders and which of them are mandatory, so the Anthropic
 * structured-output schema stays a single, cacheable shape.
 *
 * Rules the schema encodes, because they are house rules and not style:
 *  - every claim that came off the call carries `segmentIds`, the transcript
 *    segment idx values it rests on. That is the fidelity guard.
 *  - anything the writer inferred, or a process step nobody said out loud, is
 *    marked `processStep: true` and is allowed to have no citation. It is
 *    rendered in italics with the house label.
 *
 * Structured outputs reject recursive schemas and numeric/length constraints,
 * so nothing here uses .min()/.max(); optional fields are `.nullable()` rather
 * than `.optional()` (the API needs every property present).
 */
/** A single sentence with its evidence. */
export const citedItemSchema = z.object({
    text: z.string().describe('One statement, in the document language.'),
    segmentIds: z
        .array(z.number().int())
        .describe('Transcript segment idx values this statement rests on. Empty only when processStep is true.'),
    processStep: z
        .boolean()
        .describe('True when this is the writer\'s own inference or a standard process step that was NOT said on the call. Rendered with the house "(process step, not said on the call)" label.'),
});
export const momMetadataSchema = z.object({
    mnNumber: z.string().describe('Proposed document number, e.g. MN-BINSHOUQ-01.'),
    date: z.string().describe('Meeting date as written on the document, e.g. "17 September 2026".'),
    timeAndDuration: z.string().describe('e.g. "19:02 Cairo time · 27 min".'),
    channelAndLanguage: z.string().describe('e.g. "Zoom · Arabic (Kuwaiti / Egyptian)".'),
    client: z.string().describe('The real client — the person the garment is for, with the relationship that identifies her.'),
    clientOrLeadId: z.string().describe('Existing client/lead reference, or how it is described when there is none.'),
    dealId: z.string().describe('Deal reference, or "—".'),
    meetingType: z.string().describe('e.g. "First consultation · wedding dress".'),
});
export const momAttendeesSchema = z.object({
    clientSide: z.array(z.string()),
    houseSide: z.array(z.string()),
    alsoPresent: z.array(z.string()),
    hostedBy: z.string().describe('Who opened/hosted the call. Empty string when not established.'),
    silentOrInaudible: z
        .string()
        .describe('Who was present but did not speak, or whose voice was inaudible. Empty string when nobody was.'),
});
export const momClientAndOccasionSchema = z.object({
    occasion: z.string(),
    eventDate: z.string(),
    wantedBy: z.string(),
    budgetSignal: z.string(),
    pieceUnderDiscussion: z.string(),
    howSheReachedUs: z.string(),
});
export const momDesignBriefSchema = z.object({
    wants: z.array(z.string()),
    doesNotWant: z.array(z.string()),
    referencesAndTasteSignals: z.array(z.string()),
});
export const momDepartmentRowSchema = z.object({
    department: z.string(),
    impactFromThisMeeting: z.string(),
    toConfirmBeforeQuoting: z.string(),
});
export const momActionSchema = z.object({
    n: z.number().int().describe('Row number, starting at 1.'),
    action: z.string(),
    owner: z.string(),
    department: z.string(),
    due: z.string().describe('Due date exactly as it was phrased, e.g. "20 September" or "after the price quote".'),
    status: z.string().describe('Always "Open" on a new MOM.'),
    segmentIds: z.array(z.number().int()),
    processStep: z.boolean(),
});
export const momQuoteSchema = z.object({
    quote: z.string().describe('Verbatim, in the language it was said in. Never translated, in either copy.'),
    speaker: z.string().describe('Who said it, or empty string when uncertain.'),
    segmentIds: z.array(z.number().int()),
});
export const momContentSchema = z.object({
    metadata: momMetadataSchema,
    attendees: momAttendeesSchema,
    preparedBy: z.string().describe('The Account Manager. Never a tool, a model, or a vendor.'),
    source: z.string().describe('e.g. "Zoom meeting 9526 1936 856 · 17 September 2026 · recording transcript (from minute 03:21)".'),
    status: z.string().describe('"Draft" until a human finalizes it.'),
    summary: z.string().describe('One dense paragraph, three to six sentences.'),
    clientAndOccasion: momClientAndOccasionSchema.nullable(),
    designBrief: momDesignBriefSchema.nullable(),
    imagePlaceholders: z.array(z.string()).describe('Captions only — three for a client meeting, empty otherwise.'),
    perDepartment: z.array(momDepartmentRowSchema),
    discussionPoints: z
        .array(citedItemSchema)
        .describe('Used by the simpler templates (supplier / internal / management / generic). Empty for CLIENT.'),
    decisions: z.array(citedItemSchema),
    commitmentsToClient: z.array(citedItemSchema),
    actions: z.array(momActionSchema),
    openQuestions: z.array(citedItemSchema),
    nextSteps: z.string().describe('One paragraph using → between the steps.'),
    inTheClientsWords: z.array(momQuoteSchema),
    acknowledgement: z.boolean().describe('True to print the client signature block.'),
});
// ------------------------------------------------------------ generation --
/**
 * What the model is actually asked for — and it is deliberately NOT `MomContent`.
 *
 * Two constraints shape it:
 *  1. Everything the server already knows (document number, date, channel,
 *     Account Manager, image captions, status) is left out. A language model
 *     should not be re-deriving a date it was handed.
 *  2. The structured-output grammar has a hard size limit. Nested objects and
 *     `.nullable()` objects (which compile to an anyOf) are what push a document
 *     this size over it, so this schema is flat and every object is required.
 *     Measured against the live API: the flat shape below compiles, the same
 *     fields nested under `metadata` / `attendees` / a `content` wrapper do not.
 *
 * `expandMomDraft` on the server turns this back into the `MomContent` the
 * renderer, the editor and the database all use.
 */
export const momDraftActionSchema = z.object({
    action: z.string(),
    owner: z.string().describe('A person by name. Use "A → B" when one hands over to another.'),
    department: z.string(),
    due: z.string().describe('The due date exactly as it was phrased, e.g. "20 September".'),
    segmentIds: z.array(z.number().int()),
    processStep: z.boolean(),
});
export const momDraftQuoteSchema = z.object({
    quote: z.string().describe('Verbatim, in the language it was said in. Never translated, in either copy.'),
    segmentIds: z.array(z.number().int()),
});
export const momDraftFields = {
    // --- who and what ---
    client: z
        .string()
        .describe('One short line: the person the garment is for and the relationship that identifies her, e.g. "Nouf (the bride) — daughter of Suad Bin Shouq". Reasoning goes in reviewerNotes, not here.'),
    meetingType: z.string().describe('A few words, e.g. "First consultation · wedding dress".'),
    language: z
        .string()
        .describe('The language actually spoken, as the document names it, e.g. "Arabic (Kuwaiti / Egyptian)".'),
    // --- attendees ---
    clientSide: z.array(z.string()).describe('Each person on the client side, with their role and how they took part.'),
    houseSide: z.array(z.string()).describe('Each house person, with their role.'),
    alsoPresent: z.array(z.string()),
    hostedBy: z.string().describe('Who opened the call. Empty string when it was not established.'),
    silentOrInaudible: z
        .string()
        .describe('Who was present but did not speak, or could not be heard. Empty string when nobody was.'),
    preparedByAttended: z
        .boolean()
        .describe('True only if the Account Manager named in the brief actually took part in this call.'),
    // --- the document ---
    summary: z.string().describe('One dense paragraph, three to six sentences.'),
    clientAndOccasion: momClientAndOccasionSchema.describe('Client meetings only. For any other meeting type put "—" in every field.'),
    designBrief: momDesignBriefSchema.describe('Client meetings only. For any other meeting type leave all three lists empty.'),
    perDepartment: z.array(momDepartmentRowSchema),
    discussionPoints: z
        .array(citedItemSchema)
        .describe('The simpler meeting types use this. Leave it empty for a client meeting.'),
    decisions: z.array(citedItemSchema),
    commitmentsToClient: z.array(citedItemSchema),
    actions: z.array(momDraftActionSchema),
    openQuestions: z.array(citedItemSchema),
    nextSteps: z.string().describe('One paragraph using → between the steps.'),
    inTheClientsWords: z.array(momDraftQuoteSchema),
};
export const momDraftSchema = z.object(momDraftFields);
/** Call (A): the English draft plus what the reviewer needs to know about it. */
export const momDraftAnalysisSchema = z.object({
    ...momDraftFields,
    reviewerNotes: z
        .array(z.string())
        .describe('Things the human must confirm before finalizing: identity inferences, anything uncertain, contradictions left standing.'),
    numberKey: z.string().describe('Surname in capitals for the document number, e.g. BINSHOUQ or NOURA-SAAD.'),
    realClientName: z.string().describe('The person the garment is for — not whoever did the talking.'),
});
/** Call (B): the same draft, fully in Arabic. */
export const momDraftTranslationSchema = momDraftSchema;
// ---------------------------------------------------------------- sections --
export const MOM_SECTION_KEYS = [
    'metadata',
    'attendees',
    'preparedBy',
    'summary',
    'clientAndOccasion',
    'designBrief',
    'images',
    'perDepartment',
    'discussionPoints',
    'decisions',
    'commitmentsToClient',
    'actions',
    'openQuestions',
    'nextSteps',
    'inTheClientsWords',
    'acknowledgement',
];
// -------------------------------------------------------------- api shapes --
export const generateMomInputSchema = z.object({
    instruction: z.string().max(2000).optional(),
    language: z.enum(['AR', 'EN', 'BOTH']).optional(),
});
export const updateMomInputSchema = z
    .object({
    contentEn: momContentSchema.optional(),
    contentAr: momContentSchema.optional(),
    numberKey: z
        .string()
        // Case-insensitive on the way in and normalized server-side: a reviewer
        // typing "binshouq" means the same key as "BINSHOUQ". A space does not —
        // it would silently split into a different document series.
        .regex(/^[A-Za-z0-9-]{1,40}$/, 'numberKey must be letters, digits and hyphens')
        .optional(),
    reviewerNotes: z.array(z.string()).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'nothing to update' });
export const momLangQuerySchema = z.object({ lang: z.enum(['en', 'ar']).default('en') });
/** Where a rendered MOM PDF lives in Spaces. */
export function momPdfObjectKey(meetingId, version, docNumberOrDraft, lang) {
    return `moms/${meetingId}/v${version}/${docNumberOrDraft}-${lang}.pdf`;
}
//# sourceMappingURL=mom.js.map